import { Skeleton } from "@aitisal/ui/retail-storefront/skeleton";

// ==========================================
// PRODUCT CARD SKELETON (mirrors ProductCard)
// ==========================================
export function ProductCardSkeleton() {
  return (
    <div className="w-full bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="relative aspect-square bg-[#F3F3F3]">
        <Skeleton className="absolute inset-0 rounded-none" />
      </div>
      <div className="p-4 h-[148px] md:h-[80px] flex flex-col gap-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-9 w-full rounded-full md:hidden" />
      </div>
    </div>
  );
}

// ==========================================
// HOME PAGE SKELETON (mirrors c/index.tsx)
// ==========================================
export function HomePageSkeleton() {
  return (
    <div className="min-h-screen w-full bg-white font-sans text-gray-900 pb-24">
      {/* RetailBanner — full-bleed 80/33 */}
      <div className="w-full" style={{ aspectRatio: "80/33" }}>
        <Skeleton className="w-full h-full rounded-none" />
      </div>

      <div className="space-y-12">
        {/* CategoryCircular */}
        <div className="mx-auto max-w-7xl px-4 pt-6">
          <div className="flex items-center gap-6 px-12">
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-3 flex-shrink-0"
              >
                <Skeleton className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
            <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
          </div>
        </div>

        {/* NewArrivals */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Mobile: hero + horizontal scroll */}
          <div className="flex flex-col lg:hidden gap-4">
            <Skeleton className="w-full aspect-[5/4] rounded-2xl" />
            <div className="flex overflow-x-auto gap-3 pb-2 -mx-4 px-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="w-[55%] flex-none">
                  <ProductCardSkeleton />
                </div>
              ))}
            </div>
          </div>
          {/* Desktop: hero left, 2x2 grid right */}
          <div className="hidden lg:flex gap-4 items-stretch">
            <div className="flex-shrink-0 w-[52%]">
              <Skeleton className="w-full aspect-[5/6] rounded-2xl" />
            </div>
            <div className="flex-1 min-w-0 grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>

        {/* SectionGrid x2 */}
        {Array.from({ length: 2 }).map((_, s) => (
          <div key={s} className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="pt-8 pb-2">
              <div className="flex flex-row items-center justify-between">
                <Skeleton className="h-7 w-44" />
                <Skeleton className="h-8 w-24 rounded-full" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ))}

        {/* CategoryCatalogue */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6 text-center">
            <Skeleton className="h-8 w-48 mx-auto mb-2" />
            <Skeleton className="h-4 w-40 mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="w-full aspect-square rounded-xl" />
            ))}
          </div>
        </div>

        {/* MidBanner */}
        <div className="w-full bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="flex-1 text-center lg:text-left space-y-6">
                <Skeleton className="h-6 w-32 mx-auto lg:mx-0" />
                <div className="space-y-2">
                  <Skeleton className="h-12 w-48 mx-auto lg:mx-0" />
                  <Skeleton className="h-8 w-40 mx-auto lg:mx-0" />
                </div>
                <Skeleton className="h-4 w-64 mx-auto lg:mx-0" />
                <Skeleton className="h-12 w-40 mx-auto lg:mx-0" />
              </div>
              <div className="flex-1 w-full">
                <Skeleton className="w-full aspect-square rounded-2xl max-w-md mx-auto" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// PRODUCT GRID SKELETON
// ==========================================
export function ProductGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-6">
      {Array.from({ length: count }).map((_, index) => (
        <ProductCardSkeleton key={index} />
      ))}
    </div>
  );
}

// ==========================================
// PRODUCT DETAIL PAGE SKELETON
// ==========================================
export function ProductDetailSkeleton() {
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col lg:flex-row lg:gap-8">
        {/* Left Side: Image Gallery Skeleton */}
        <div className="flex-1">
          <div className="flex gap-4">
            <div className="hidden lg:flex flex-col gap-3 lg:max-h-[640px] lg:overflow-y-auto no-scrollbar">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="w-20 h-20 rounded-xl" />
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <Skeleton className="w-full aspect-square rounded-2xl" />
            </div>
          </div>
        </div>

        {/* Right Side: Product Info Skeleton */}
        <div className="flex-1 mt-6 lg:mt-0 lg:pl-8">
          <div className="flex justify-between items-start mb-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-12 w-12 rounded-full" />
          </div>

          <div className="flex items-baseline gap-3 mb-6">
            <Skeleton className="h-12 w-32" />
            <Skeleton className="h-6 w-24" />
          </div>

          <div className="space-y-2 mb-8">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>

          {/* Customizations Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="border border-gray-200 rounded-lg p-4">
              <Skeleton className="h-5 w-32 mb-3" />
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded-lg" />
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons Skeleton */}
          <div className="flex flex-row gap-4 mb-6">
            <Skeleton className="h-12 w-[140px] rounded-xl" />
            <Skeleton className="h-12 flex-1 rounded-xl" />
          </div>

          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

// ==========================================
// PRODUCT LISTING PAGE SKELETON
// ==========================================
export function ProductListingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-4">
        {/* Breadcrumb */}
        <div className="px-4 mb-4">
          <Skeleton className="h-4 w-64" />
        </div>

        {/* Page Heading */}
        <div className="px-4 mb-6">
          <Skeleton className="h-9 w-48" />
        </div>

        <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
          {/* Filter Sidebar Skeleton */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-5 w-16" />
              </div>

              {/* Filter Sections */}
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="mb-6 border-b border-gray-200 pb-4">
                  <Skeleton className="h-5 w-32 mb-3" />
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <Skeleton key={j} className="h-5 w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* Product Grid */}
          <main className="flex-1 min-w-0">
            <div className="mb-4 sm:mb-6 flex items-center justify-between">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-10 w-48 rounded-lg" />
            </div>

            <ProductGridSkeleton count={8} />
          </main>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// CHECKOUT PAGE SKELETON
// ==========================================
export function CheckoutSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <Skeleton className="h-4 w-48" />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Form Skeleton */}
          <div className="lg:col-span-2">
            <div className="border border-gray-200 rounded-lg mb-6">
              <div className="p-6 border-b">
                <Skeleton className="h-6 w-40" />
              </div>

              <div className="px-6 pb-6 pt-4 space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-10 w-full rounded-md" />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-10 w-full rounded-md" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-10 w-full rounded-md" />
                </div>

                {/* Address Field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48 mb-2" />
                  <Skeleton className="h-24 w-full rounded-md" />
                </div>
              </div>
            </div>

            <Skeleton className="h-12 w-full rounded-lg" />
          </div>

          {/* Right Column: Cart Summary Skeleton */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <Skeleton className="h-6 w-32 mb-6" />

              <div className="space-y-4 mb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-4 pb-4 border-b">
                    <Skeleton className="w-16 h-16 rounded-md" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between pt-2">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// SMALL PRODUCT CARD SKELETON
// ==========================================
export function SmallProductCardSkeleton() {
  return (
    <div className="flex gap-3 p-2 bg-white border border-gray-200 rounded-lg">
      <Skeleton className="w-20 h-20 rounded-md flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
  );
}

export { Skeleton };
